import React, { useState } from "react";
import { useEditorStore } from "../../../../../state/editorStore";
import { OutlineToolIcon } from "../../../../../icons";
import ToolPrepSliderField from "../../../shared/ToolPrepSliderField";
import BackgroundControlColorMenu from "../../../../editor/panels/background/BackgroundControlColorMenu";
import "../../../shared/ToolPrepObjectControls.css";
import styles from "./Outline.module.css";

const DEFAULT_OUTLINE = {
  enabled: false,
  width: 0,
  blur: 0,
  color: "#000000"
};

const isDefaultOutline = (outline) =>
  outline.width === 0 &&
  outline.blur === 0 &&
  outline.color === "#000000";

const Outline = ({ defaultExpanded = false, forceExpanded = false }) => {
  const outline = useEditorStore((s) => s.object.outline);
  const setOutline = useEditorStore((s) => s.setOutline);
  const pushSnapshot = useEditorStore((s) => s.pushSnapshot);

  const [expanded, setExpanded] = useState(
    Boolean(defaultExpanded || forceExpanded)
  );
  const isExpanded = forceExpanded || expanded;

  const updateOutline = (updates, options = {}) => {
    const newOutline = {
      ...outline,
      ...updates,
      enabled: true
    };

    if (isDefaultOutline(newOutline)) {
      setOutline(DEFAULT_OUTLINE, options);
    } else {
      setOutline(newOutline, options);
    }
  };

  const commitSliderChange = () => {
    pushSnapshot();
  };

  const handleOutlineColor = (color) => {
    if (!color || color === "transparent") return;
    updateOutline({ color });
  };

  const prepLayout = forceExpanded;

  const colorSection = (
    <div className={styles.colorOptions}>
      {[
        "#000000",
        "#ffffff",
        "#3b82f6",
        "#22c55e",
        "#f59e0b",
        "#a855f7"
      ].map((color) => (
        <button
          key={color}
          className={`${styles.colorCircle} ${
            outline.color === color ? styles.activeColor : ""
          }`}
          style={{ background: color }}
          onClick={(e) => {
            e.stopPropagation();
            updateOutline({ color });
          }}
        />
      ))}

      <label
        className={styles.colorPickerWrapper}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className={styles.colorPickerInner}
          style={{ background: outline.color }}
        />
        <input
          type="color"
          value={outline.color}
          onChange={(e) => updateOutline({ color: e.target.value })}
          className={styles.colorPicker}
        />
      </label>
    </div>
  );

  return (
    <div
      className={`${styles.card}${prepLayout ? ` ${styles.prepCard}` : ""}`}
      onClick={() => {
        if (!forceExpanded) setExpanded((prev) => !prev);
      }}
    >
      {!prepLayout ? (
        <div className={styles.header}>
          <div className={styles.left}>
            <span className={styles.icon}>
              <OutlineToolIcon />
            </span>
            <span className={styles.title}>Outline</span>
          </div>

          <div className={styles.right}>
            <label
              className={styles.switch}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={outline.enabled}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setOutline(DEFAULT_OUTLINE);
                  } else {
                    setOutline({
                      ...outline,
                      enabled: true
                    });
                  }
                }}
              />
              <span className={styles.slider}></span>
            </label>

            <button
              type="button"
              className={`${styles.chevron} ${
                isExpanded ? styles.rotate : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((prev) => !prev);
              }}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Collapse outline" : "Expand outline"}
            >
              ›
            </button>
          </div>
        </div>
      ) : null}

      {isExpanded && (
        <div
          className={`${styles.body}${prepLayout ? ` ${styles.prepBody}` : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {prepLayout ? (
            <div className="crop-prep-controls object-prep-controls">
              <ToolPrepSliderField
                label="Width"
                value={outline.width}
                min={0}
                max={20}
                unit="px"
                maxInputLength={2}
                onChange={(v) =>
                  updateOutline({ width: v }, { recordHistory: false })
                }
                onCommit={commitSliderChange}
              />
              <ToolPrepSliderField
                label="Blur"
                value={outline.blur}
                min={0}
                max={20}
                unit="px"
                maxInputLength={2}
                onChange={(v) =>
                  updateOutline({ blur: v }, { recordHistory: false })
                }
                onCommit={commitSliderChange}
              />

              <div className="crop-prep-controls__section otp-outline-color-menu">
                <BackgroundControlColorMenu
                  currentBgColor={outline.color}
                  onColorSelect={handleOutlineColor}
                  onApplyBackground={handleOutlineColor}
                />
              </div>
            </div>
          ) : (
            <>
              <Slider
                label="Width"
                value={outline.width}
                min={0}
                max={20}
                onChange={(v) =>
                  updateOutline({ width: v }, { recordHistory: false })
                }
                onDragStart={commitSliderChange}
              />

              <Slider
                label="Blur"
                value={outline.blur}
                min={0}
                max={20}
                onChange={(v) =>
                  updateOutline({ blur: v }, { recordHistory: false })
                }
                onDragStart={commitSliderChange}
              />

              <div className={styles.colorSection}>
                <div className={styles.colorLabel}>Color</div>
                {colorSection}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Slider = ({ label, value, min, max, onChange, onDragStart }) => (
  <div className={styles.sliderRow}>
    <div className={styles.sliderTop}>
      <span>{label}</span>
      <span>{value}px</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      onPointerDown={() => onDragStart?.()}
      className={styles.range}
    />
  </div>
);

export default Outline;